<script setup>
import { useAxios } from '@/hooks/useAxios';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const isVerified = ref(false);
const ValidationMsg = ref('');
const RightMsg = ref('');
const errMessage = ref(props.errmsg);

onMounted(async () => {
    // await Promise.all([searchDriver()]);
    // nextTick(() => {
    //     filteredOptions.value = driverData.value;
    // });
});

watch(
    () => props.modelValue,
    (newVal) => {
        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
        RightMsg.value = '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    let value = event.target.value.toLowerCase();
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';
    RightMsg.value = '';
    isVerified.value = false;

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (!validateLoginId(value)) return;

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서 위치 조정
    // if (lengthDiff !== 0) {
    //     cursorPosition = cursorPosition + lengthDiff;
    // }

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value
    });
};

const checkDuplicate = async () => {
    try {
        if (inputValue.value === '') {
            ValidationMsg.value = '아이디를 입력하세요.';
            return false;
        }

        if (!validateLoginId(inputValue.value)) return;

        const url = `/api/manager/login-check/${inputValue.value}`;
        const config = {
            method: 'get'
        };
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                //console.log(response.data.data);
                if (!response.data.success) {
                    console.log('중복체크');
                } else {
                    isVerified.value = true;
                    RightMsg.value = '사용 가능한 아이디 입니다.';
                }
            },
            onError(err) {
                if (!err.response.data.success) {
                    ValidationMsg.value = '이미 사용 중인 아이디 입니다.';
                }
                // console.error('Error:', err.message);
            }
        });
        setHeader('Content-Type', 'application/json');
        execute();
    } catch (error) {
        console.error('중복 체크 중 오류 발생:', error);

        ValidationMsg.value = '중복 체크 중 오류가 발생했습니다.';
    }
};

const validateLoginId = (loginId) => {
    const regex = /^[a-z0-9-_]{6,12}$/;
    // if (loginId === '') {
    //     ValidationMsg.value = '아이디를 입력하세요.';
    //     return false;
    // }

    if (!regex.test(loginId) && loginId !== '') {
        ValidationMsg.value = '아이디는 소문자, 숫자포함 6 ~ 12 자리 입니다.';
        return false;
    }
    ValidationMsg.value = '';
    return true;
};

const clearInput = () => {
    ValidationMsg.value = '';
    errMessage.value = '';
    RightMsg.value = '';
    isVerified.value = false;
    emitUpdate('', 'input');
};

defineExpose({
    isVerified
});
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            <span v-if="!ValidationMsg && RightMsg" class="right-message">{{ RightMsg }}</span>
        </label>

        <div class="relative flex flex-col items-center w-full">
            <div class="relative flex items-center w-full h-12">
                <input
                    v-if="visible"
                    type="text"
                    :id="inputId"
                    v-model="inputValue"
                    class="block w-full p-3 ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    :class="{
                        'pr-24': button,
                        'error-border': ValidationMsg
                    }"
                    required
                    placeholder="아이디를 입력하세요."
                    :required="required"
                    @input="onInput"
                />

                <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                    <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(6.5rem)]">&times;</button>
                    <button v-if="!isReadonly" @click="checkDuplicate" class="button-style">중복확인</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.right-message {
    color: green;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 1.4rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}
</style>
